import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useTelegram } from '@/hooks/useTelegram';
import { GuildsPage } from '@/pages/Social/GuildsPage';
import { SubscriptionPage } from '@/pages/Subscription/SubscriptionPage';
import { SettingsPage } from '@/pages/Settings/SettingsPage';
import { CLASS_DATA, RACE_NAMES, formatModifier } from '@/types';
import type { CharacterClass, Race } from '@/types';

type ActiveScreen = 'home' | 'guilds' | 'subscription' | 'settings';

export function HomePage() {
  const navigate = useNavigate();
  const { user, character } = useAuth();
  const { hapticFeedback } = useTelegram();

  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('home');

  const classData = character?.characterClass
    ? CLASS_DATA[character.characterClass as CharacterClass]
    : null;

  const raceName = character?.race
    ? RACE_NAMES[character.race as Race]
    : null;

  // XP needed for next level in Dungeon World: Level + 7
  const xpForNextLevel = character ? character.level + 7 : 8;
  const xpProgress = character
    ? Math.round((character.xp / xpForNextLevel) * 100)
    : 0;

  // Show active screen
  if (activeScreen === 'guilds') {
    return (
      <GuildsPage
        onClose={() => setActiveScreen('home')}
        currentUserId={user?.id}
      />
    );
  }

  if (activeScreen === 'subscription') {
    return (
      <SubscriptionPage onClose={() => setActiveScreen('home')} />
    );
  }

  if (activeScreen === 'settings') {
    return (
      <SettingsPage onClose={() => setActiveScreen('home')} />
    );
  }

  // Home screen
  return (
    <div className="min-h-screen bg-gray-900 safe-area-top safe-area-bottom">
      {/* Top navigation */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={() => {
            hapticFeedback('light');
            navigate('/mode-select');
          }}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Меню
        </button>
        <div className="flex gap-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('settings');
            }}
            className="w-10 h-10 rounded-full bg-gray-800/50 border border-gray-700/50 flex items-center justify-center"
          >
            <span className="text-lg">⚙️</span>
          </motion.button>
        </div>
      </div>

      {/* Header with character info */}
      <div className="px-4 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
            <span className="text-3xl">⚔️</span>
          </div>

          <div className="flex-1">
            <h1 className="text-lg font-bold text-white">
              {character?.name || user?.firstName || 'Искатель приключений'}
            </h1>
            <p className="text-sm text-gray-400">
              {classData?.nameRu || 'Нет класса'} • Ур. {character?.level || 1}
              {raceName && ` • ${raceName}`}
            </p>

            {/* XP Progress bar */}
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>{character?.xp || 0} / {xpForNextLevel} XP</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(xpProgress, 100)}%` }}
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full"
                />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Character Stats */}
      {character && character.characterClass && (
        <div className="p-4">
          <h2 className="text-sm font-medium text-gray-400 mb-3">Характеристики</h2>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatCard label="СИЛ" value={character.strength} />
            <StatCard label="ЛОВ" value={character.dexterity} />
            <StatCard label="ТЕЛ" value={character.constitution} />
            <StatCard label="ИНТ" value={character.intelligence} />
            <StatCard label="МДР" value={character.wisdom} />
            <StatCard label="ХАР" value={character.charisma} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="bg-red-900/30 rounded-xl p-3 text-center">
              <p className="text-xs text-red-400">HP</p>
              <p className="text-lg font-bold text-white">
                {character.currentHp ?? 0}/{character.maxHp ?? 0}
              </p>
            </div>
            <div className="bg-blue-900/30 rounded-xl p-3 text-center">
              <p className="text-xs text-blue-400">Броня</p>
              <p className="text-lg font-bold text-white">{character.armor ?? 0}</p>
            </div>
            <div className="bg-yellow-900/30 rounded-xl p-3 text-center">
              <p className="text-xs text-yellow-400">Монеты</p>
              <p className="text-xs font-bold text-white">
                {character.copper ?? 0}м {character.silver ?? 0}с {character.gold ?? 0}з
              </p>
            </div>
          </div>
        </div>
      )}

      {/* No character - prompt to create */}
      {(!character || !character.characterClass) && (
        <div className="p-4">
          <div className="bg-gray-800/50 rounded-xl p-6 text-center">
            <span className="text-4xl mb-4 block">🎭</span>
            <h2 className="text-lg font-bold text-white mb-2">Создайте персонажа</h2>
            <p className="text-sm text-gray-400 mb-4">
              Выберите класс и начните своё приключение в мире Dungeon World!
            </p>
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-medium rounded-xl"
              onClick={() => {
                hapticFeedback('medium');
                navigate('/character-creation');
              }}
            >
              Создать персонажа
            </motion.button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-4">
        <h2 className="text-sm font-medium text-gray-400 mb-3">Приключение</h2>

        <div className="space-y-3">
          <ActionCard
            emoji="⚔️"
            title="Начать приключение"
            description="Новая сессия с AI-мастером"
            onClick={() => {
              hapticFeedback('medium');
              navigate('/adventure');
            }}
            disabled={!character?.characterClass}
          />

          <ActionCard
            emoji="🎒"
            title="Инвентарь"
            description="Снаряжение и предметы"
            onClick={() => {
              hapticFeedback('light');
              navigate('/inventory');
            }}
            disabled={!character?.characterClass}
          />
        </div>
      </div>

      {/* Quick links */}
      <div className="p-4">
        <h2 className="text-sm font-medium text-gray-400 mb-3">Сообщество</h2>

        <div className="grid grid-cols-2 gap-3">
          <QuickLink
            emoji="👥"
            title="Группы"
            onClick={() => {
              hapticFeedback('light');
              setActiveScreen('guilds');
            }}
          />
          <QuickLink
            emoji="🏆"
            title="Рейтинг"
            onClick={() => {
              hapticFeedback('light');
              // TODO: Show leaderboard
            }}
          />
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-800/30 rounded-xl p-3 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
      <p className="text-xs text-gray-400">{formatModifier(value)}</p>
    </div>
  );
}

interface ActionCardProps {
  emoji: string;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function ActionCard({ emoji, title, description, onClick, disabled }: ActionCardProps) {
  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`w-full p-4 rounded-xl text-left transition-all flex items-center gap-4 ${
        disabled
          ? 'bg-gray-900/30 opacity-50'
          : 'bg-gray-800/50 hover:bg-gray-800/70 active:bg-gray-800'
      }`}
    >
      <div className="w-12 h-12 rounded-xl bg-gray-700/50 flex items-center justify-center flex-shrink-0">
        <span className="text-2xl">{emoji}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white">{title}</p>
        <p className="text-sm text-gray-400 truncate">{description}</p>
      </div>
      {disabled && <span className="text-xs text-gray-600">🔒</span>}
    </motion.button>
  );
}

interface QuickLinkProps {
  emoji: string;
  title: string;
  onClick?: () => void;
  disabled?: boolean;
}

function QuickLink({ emoji, title, onClick, disabled }: QuickLinkProps) {
  return (
    <motion.button
      whileTap={{ scale: disabled ? 1 : 0.95 }}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`p-4 rounded-xl flex items-center gap-3 ${
        disabled
          ? 'bg-gray-900/30 opacity-50'
          : 'bg-gray-800/50 hover:bg-gray-800/70'
      }`}
    >
      <span className="text-xl">{emoji}</span>
      <span className="text-sm font-medium text-white">{title}</span>
      {disabled && <span className="text-xs text-gray-600 ml-auto">Скоро</span>}
    </motion.button>
  );
}
